import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  FaSearch,
  FaChevronDown,
  FaTimes,
  FaPlus,
} from "react-icons/fa";
import { getDoctors, createDoctor } from "../services/doctorService";
import { useAuth } from "../auth/AuthContext";
import AddDoctorModal from "../components/doctor/AddDoctorModal";
import NotificationToast from "../components/common/NotificationToast";
import { DOCTOR_LIST_URL_KEY } from "../utils/subscriberHelpers";

// Scroll offset saved when a row is opened, keyed by the list URL it was opened from,
// so Back lands on the same rows rather than the top of the page.
const SCROLL_KEY = "doctorList:scroll";

const DoctorList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, loading: authLoading } = useAuth();

  // State for API data
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusOptions, setStatusOptions] = useState([]);
  const [specializationOptions, setSpecializationOptions] = useState([]);

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalMounted, setModalMounted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);
  const [notificationMounted, setNotificationMounted] = useState(false);

  // Search state - initialize from URL or empty
  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [searchInput, setSearchInput] = useState(
    () => searchParams.get("search") || ""
  );
  const searchTimeoutRef = useRef(null);

  // Filter states
  // Note: Date filter UI is not implemented, but state is kept for URL param reading
  // const [dateFilter, _setDateFilter] = useState(
  //   () => searchParams.get("date_filter") || ""
  // );
  // const [startDate, _setStartDate] = useState(
  //   () => searchParams.get("start_date") || ""
  // );
  // const [endDate, _setEndDate] = useState(
  //   () => searchParams.get("end_date") || ""
  // );

  // Custom DatePicker Input Component
  // const CustomDateInput = React.forwardRef(
  //   ({ value, onClick, placeholder, clearDate }, ref) => (
  //     <div className="relative w-full">
  //       <button
  //         type="button"
  //         onClick={onClick}
  //         ref={ref}
  //         className="w-full px-3 py-1.5 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm text-left hover:border-gray-400 transition-colors"
  //       >
  //         <span
  //           className={
  //             value
  //               ? "text-gray-900 block truncate pr-8"
  //               : "text-gray-400 block truncate pr-8"
  //           }
  //         >
  //           {value || placeholder}
  //         </span>
  //       </button>
  //       <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 pointer-events-none">
  //         {value && clearDate && (
  //           <button
  //             type="button"
  //             onClick={(e) => {
  //               e.stopPropagation();
  //               clearDate();
  //             }}
  //             className="text-gray-400 hover:text-gray-600 p-0.5 rounded hover:bg-gray-100 pointer-events-auto"
  //             aria-label="Clear date"
  //           >
  //             <FaTimes className="w-3 h-3" />
  //           </button>
  //         )}
  //         <FaCalendarAlt className="text-gray-400 w-4 h-4 pointer-events-none" />
  //       </div>
  //     </div>
  //   )
  // );
  // CustomDateInput.displayName = "CustomDateInput";

  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const statusParam = searchParams.get("status");
    return statusParam ? statusParam.split(",") : [];
  });
  const [selectedSpecializations, setSelectedSpecializations] = useState(() => {
    const specParam = searchParams.get("specializations");
    return specParam ? specParam.split(",") : [];
  });

  // Dropdown open states
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [specializationDropdownOpen, setSpecializationDropdownOpen] =
    useState(false);

  // Auto-detect role from authentication
  const currentRole = (() => {
    const authRole = localStorage.getItem("authRole");
    const roleMapping = {
      wallet_superadmin: "SuperAdmin",
      wallet_finance: "Finance",
      wallet_ops: "Ops",
      wallet_support: "Support",
    };
    return roleMapping[authRole] || null;
  })();

  // Check if user can create doctors (Finance and SuperAdmin only, not Ops)
  const canCreateDoctor = currentRole === 'Finance' || currentRole === 'SuperAdmin';

  const PAGE_SIZE = 25;
  // Initialize page from URL or default to 1
  const [page, setPage] = useState(() => {
    const pageParam = searchParams.get("page");
    return pageParam ? parseInt(pageParam, 10) : 1;
  });
  const [pagination, setPagination] = useState({
    page: 1,
    items: PAGE_SIZE,
    total: 0,
    total_pages: 0,
  });

  // Track if initial mount is complete to prevent duplicate calls
  const isInitialMountRef = useRef(true);
  // Track last API call parameters to prevent duplicate calls
  const lastApiCallRef = useRef({});
  // Track if API call is in progress to prevent concurrent calls
  const isLoadingRef = useRef(false);

  // Initialize modal mount state
  useEffect(() => {
    setModalMounted(true);
    setNotificationMounted(true);
    return () => {
      setModalMounted(false);
      setNotificationMounted(false);
    };
  }, []);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();

    if (page > 1) params.set("page", page);
    if (search) params.set("search", search);
    if (selectedStatuses.length)
      params.set("status", selectedStatuses.join(","));
    if (selectedSpecializations.length)
      params.set("specializations", selectedSpecializations.join(","));

    setSearchParams(params, { replace: true });
  }, [page, search, selectedStatuses, selectedSpecializations, setSearchParams]);

  // Handle search submission (Enter key or debounce)
  const handleSearchSubmit = useCallback(() => {
    const newSearch = searchInput.trim();
    if (newSearch !== search) {
      setSearch(newSearch);
      setPage(1); // Reset to page 1 when search changes
    }
    // Clear any pending timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
  }, [searchInput, search]);

  // Debounce search input (2 seconds)
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      handleSearchSubmit();
    }, 2000);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInput, handleSearchSubmit]);

  // Load doctors from API
  useEffect(() => {
    // Skip on initial mount if auth is still loading
    if (authLoading) {
      return;
    }

    // Skip if not authenticated or no role
    if (!isAuthenticated || !currentRole) {
      return;
    }

    // Build API params
    const apiParams = {
      page: page || 1,
      items: PAGE_SIZE,
    };

    if (search.trim()) apiParams.search = search.trim();
    if (selectedStatuses.length > 0) apiParams.status = selectedStatuses;
    if (selectedSpecializations.length > 0)
      apiParams.specializations = selectedSpecializations;

    // Create a key for comparison
    const apiCallKey = JSON.stringify(apiParams);

    // Prevent duplicate API calls with same parameters
    if (lastApiCallRef.current.key === apiCallKey) {
      return;
    }

    // Prevent concurrent API calls
    if (isLoadingRef.current) {
      return;
    }

    // Update last API call reference
    lastApiCallRef.current = { key: apiCallKey, params: apiParams };

    const loadDoctors = async () => {
      isLoadingRef.current = true;
      try {
        setLoading(true);

        const token = localStorage.getItem("authToken");
        if (!token) {
          navigate("/login");
          return;
        }

        const response = await getDoctors(currentRole, apiParams);

        let doctorsData = null;
        let paginationData = null;

        // Handle response structure: { msg: { items: [...], pagination: {...}, filters: {...} }, metadata: {...} }
        if (response && response.msg) {
          if (response.msg.items && Array.isArray(response.msg.items)) {
            doctorsData = response.msg.items;
          }
          if (response.msg.pagination) {
            paginationData = response.msg.pagination;
          }
          // Extract filter options from API response
          if (response.msg.filters) {
            if (
              response.msg.filters.status_options &&
              Array.isArray(response.msg.filters.status_options)
            ) {
              setStatusOptions(response.msg.filters.status_options);
            }
            if (
              response.msg.filters.specialization_options &&
              Array.isArray(response.msg.filters.specialization_options)
            ) {
              setSpecializationOptions(
                response.msg.filters.specialization_options
              );
            }
          }
        } else if (response && response.data) {
          if (response.data.items && Array.isArray(response.data.items)) {
            doctorsData = response.data.items;
          }
          if (response.data.pagination) {
            paginationData = response.data.pagination;
          }
          // Extract filter options from alternative response structure
          if (response.data.filters) {
            if (
              response.data.filters.status_options &&
              Array.isArray(response.data.filters.status_options)
            ) {
              setStatusOptions(response.data.filters.status_options);
            }
            if (
              response.data.filters.specialization_options &&
              Array.isArray(response.data.filters.specialization_options)
            ) {
              setSpecializationOptions(
                response.data.filters.specialization_options
              );
            }
          }
        } else if (Array.isArray(response)) {
          doctorsData = response;
          paginationData = {
            page: 1,
            items: response.length,
            total: response.length,
            total_pages: Math.ceil(response.length / PAGE_SIZE),
          };
        }

        if (doctorsData && Array.isArray(doctorsData)) {
          setDoctors(doctorsData);
          if (paginationData) {
            // Calculate total_pages from total and items (backend doesn't provide total_pages)
            const calculatedTotalPages = Math.ceil(
              paginationData.total / paginationData.items
            );
            setPagination({
              ...paginationData,
              total_pages: calculatedTotalPages,
            });
          } else {
            setPagination({
              page: page || 1,
              items: PAGE_SIZE,
              total: doctorsData.length,
              total_pages: Math.ceil(doctorsData.length / PAGE_SIZE),
            });
          }
        } else {
        setDoctors([]);
        setPagination({
          page: 1,
          items: PAGE_SIZE,
          total: 0,
          total_pages: 0,
        });
        }
      } catch (error) {
        console.error("Error loading doctors:", error);
        // Handle API errors gracefully - show empty state instead of error page
        // This allows the page to still be functional even if API doesn't exist
        setDoctors([]);
        setPagination({
          page: 1,
          items: PAGE_SIZE,
          total: 0,
          total_pages: 0,
        });
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
        if (isInitialMountRef.current) {
          isInitialMountRef.current = false;
        }
      }
    };

    loadDoctors();
  }, [
    currentRole,
    isAuthenticated,
    authLoading,
    navigate,
    page,
    search,
    selectedStatuses,
    selectedSpecializations,
  ]);

  // Restore the scroll offset once the rows it was measured against are back.
  useEffect(() => {
    if (loading || doctors.length === 0) return;
    try {
      const saved = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || "null");
      if (saved) sessionStorage.removeItem(SCROLL_KEY);
      if (!saved || saved.search !== location.search) return;
      // The table settles over a few frames; keep nudging until the page is tall enough.
      let frames = 0;
      const restore = () => {
        window.scrollTo(0, saved.y);
        if (Math.abs(window.scrollY - saved.y) > 2 && frames++ < 30) requestAnimationFrame(restore);
      };
      requestAnimationFrame(restore);
    } catch {
      // sessionStorage unavailable — Back still keeps page and filters via the URL.
    }
  }, [loading, doctors, location.search]);

  const openDoctor = (path) => {
    const from = `${location.pathname}${location.search}`;
    try {
      sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ search: location.search, y: window.scrollY }));
      sessionStorage.setItem(DOCTOR_LIST_URL_KEY, from);
    } catch {
      // ignore — scroll restore is a convenience
    }
    navigate(path, { state: { from } });
  };

  const handleStatusChange = (statusValue) => {
    setSelectedStatuses((prev) => {
      if (prev.includes(statusValue)) {
        return prev.filter((s) => s !== statusValue);
      } else {
        return [...prev, statusValue];
      }
    });
    setPage(1);
  };

  const handleSpecializationChange = (specValue) => {
    setSelectedSpecializations((prev) => {
      if (prev.includes(specValue)) {
        return prev.filter((s) => s !== specValue);
      } else {
        return [...prev, specValue];
      }
    });
    setPage(1);
  };

  const clearStatusFilters = () => {
    setSelectedStatuses([]);
    setPage(1);
  };

  const clearSpecializationFilters = () => {
    setSelectedSpecializations([]);
    setPage(1);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".status-dropdown-container")) {
        setStatusDropdownOpen(false);
      }
      if (!event.target.closest(".specialization-dropdown-container")) {
        setSpecializationDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle add doctor modal
  const handleOpenAddModal = () => {
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    if (!submitting) {
      setShowAddModal(false);
    }
  };

  const handleAddDoctor = async (doctorData) => {
    setSubmitting(true);
    try {
      await createDoctor(currentRole, doctorData);

      // Show success notification
      setNotification({
        type: "success",
        message: "Doctor added successfully!",
      });

      // Close modal
      setShowAddModal(false);

      // Refresh the list
      lastApiCallRef.current = {};
      window.location.reload(); // Simple refresh - could be optimized to refetch
    } catch (error) {
      console.error("Error adding doctor:", error);
      setNotification({
        type: "error",
        message: error.message || "Failed to add doctor. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification(null);
  };

  if (!currentRole) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">
            Access Denied
          </h2>
          <p className="text-gray-600">
            You don't have permission to access the Doctor List.
          </p>
          <button
            onClick={() => navigate("/subscriber-wallet")}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  // Show loading spinner only on initial load
  if (loading && doctors.length === 0 && isInitialMountRef.current) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Don't show error page - always show the list page with empty state if needed

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-7xl space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate("/subscriber-wallet")}
            className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
          >
            ← Back to Home
          </button>
        </div>

        <div className="bg-white rounded-md shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <span className="mr-2">Doctors List</span>
              </h2>
              {canCreateDoctor && (
                <button
                  onClick={handleOpenAddModal}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 flex items-center gap-2 transition-colors"
                >
                  <FaPlus className="w-3 h-3" />
                  Add Doctor
                </button>
              )}
            </div>

            {/* Search */}
            <div className="mb-4">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by Name or Mobile no..."
                  className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearchSubmit();
                    }
                  }}
                />
              </div>
            </div>

            {/* Filters */}
            <div className="space-y-4">
              {/* Date Filter Buttons */}

              {/* Status and Specialization Filters - Side by Side */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Status Filter Dropdown */}
                <div className="status-dropdown-container">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Status
                    </label>
                    {selectedStatuses.length > 0 && (
                      <button
                        onClick={clearStatusFilters}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                      className="w-full px-4 py-2 text-left bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center justify-between hover:border-gray-400 transition-colors"
                    >
                      <span className="text-sm text-gray-700">
                        {selectedStatuses.length === 0
                          ? "All Status"
                          : selectedStatuses.length === 1
                            ? statusOptions.find(
                                (opt) => opt.value === selectedStatuses[0]
                              )?.label || "1 selected"
                            : `${selectedStatuses.length} selected`}
                      </span>
                      <FaChevronDown
                        className={`w-4 h-4 text-gray-400 transition-transform ${statusDropdownOpen ? "transform rotate-180" : ""}`}
                      />
                    </button>

                    {statusDropdownOpen && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {statusOptions.length === 0 ? (
                          <div className="px-4 py-2 text-sm text-gray-500">
                            No options available
                          </div>
                        ) : (
                          statusOptions.map((option) => (
                            <label
                              key={option.value}
                              className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selectedStatuses.includes(
                                  option.value
                                )}
                                onChange={() =>
                                  handleStatusChange(option.value)
                                }
                                className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="text-sm text-gray-700">
                                {option.label}
                              </span>
                            </label>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  {selectedStatuses.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {selectedStatuses.map((statusValue) => {
                        const option = statusOptions.find(
                          (opt) => opt.value === statusValue
                        );
                        return option ? (
                          <span
                            key={statusValue}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-md"
                          >
                            {option.label}
                            <button
                              type="button"
                              onClick={() => handleStatusChange(statusValue)}
                              className="ml-1.5 inline-flex items-center text-blue-600 hover:text-blue-800"
                            >
                              <FaTimes className="w-3 h-3" />
                            </button>
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>

                {/* Specialization Filter Dropdown */}
                <div className="specialization-dropdown-container">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Specialization
                    </label>
                    {selectedSpecializations.length > 0 && (
                      <button
                        onClick={clearSpecializationFilters}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() =>
                        setSpecializationDropdownOpen(
                          !specializationDropdownOpen
                        )
                      }
                      className="w-full px-4 py-2 text-left bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center justify-between hover:border-gray-400 transition-colors"
                    >
                      <span className="text-sm text-gray-700">
                        {selectedSpecializations.length === 0
                          ? "All Specializations"
                          : selectedSpecializations.length === 1
                            ? specializationOptions.find(
                                (opt) =>
                                  opt.value === selectedSpecializations[0]
                              )?.label || "1 selected"
                            : `${selectedSpecializations.length} selected`}
                      </span>
                      <FaChevronDown
                        className={`w-4 h-4 text-gray-400 transition-transform ${specializationDropdownOpen ? "transform rotate-180" : ""}`}
                      />
                    </button>

                    {specializationDropdownOpen && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {specializationOptions.length === 0 ? (
                          <div className="px-4 py-2 text-sm text-gray-500">
                            No options available
                          </div>
                        ) : (
                          specializationOptions.map((option) => (
                            <label
                              key={option.value}
                              className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selectedSpecializations.includes(
                                  option.value
                                )}
                                onChange={() =>
                                  handleSpecializationChange(option.value)
                                }
                                className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="text-sm text-gray-700">
                                {option.label}
                              </span>
                            </label>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  {selectedSpecializations.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {selectedSpecializations.map((specValue) => {
                        const option = specializationOptions.find(
                          (opt) => opt.value === specValue
                        );
                        return option ? (
                          <span
                            key={specValue}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-md"
                          >
                            {option.label}
                            <button
                              type="button"
                              onClick={() =>
                                handleSpecializationChange(specValue)
                              }
                              className="ml-1.5 inline-flex items-center text-blue-600 hover:text-blue-800"
                            >
                              <FaTimes className="w-3 h-3" />
                            </button>
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date & Time
                  </th> */}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Specialization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Qualification
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Experience
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    City
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {doctors.length === 0 ? (
                  <tr>
                    <td
                      colSpan="9"
                      className="px-6 py-8 text-center text-gray-500"
                    >
                      {loading ? "Loading..." : "No doctors found"}
                    </td>
                  </tr>
                ) : (
                  doctors.map((doctor) => (
                    <tr
                      key={doctor.id || doctor.doctor_id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => {
                        const doctorId = doctor.id || doctor.doctor_id;
                        if (doctorId) {
                          openDoctor(
                            `/subscriber-wallet/doctors/${doctorId}/slots`
                          );
                        }
                      }}
                    >
                      {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {doctor.created_at
                          ? formatDateTimeIST(doctor.created_at)
                          : "N/A"}
                      </td> */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {[doctor.first_name, doctor.last_name]
                          .filter(Boolean)
                          .join(" ") || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {Array.isArray(doctor.specializations)
                          ? doctor.specializations.join(", ")
                          : Array.isArray(doctor.specialization)
                          ? doctor.specialization.join(", ")
                          : doctor.specializations || doctor.specialization || "N/A"}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {Array.isArray(doctor.qualifications)
                          ? doctor.qualifications.join(", ")
                          : doctor.qualifications || "N/A"}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {doctor.years_of_experience
                          ? `${doctor.years_of_experience} years`
                          : "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {doctor.mobile || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {doctor.email || "N/A"}
                      </td>
                      {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {doctor?.addresses?.city || "N/A"}
                      </td> */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {doctor?.addresses?.[0]?.city || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            doctor.active === true
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {doctor.active === true ? "Active" : "Inactive"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.total > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {(pagination.page - 1) * pagination.items + 1}-
                {Math.min(pagination.page * pagination.items, pagination.total)}{" "}
                of {pagination.total}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  disabled={pagination.page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={`px-3 py-1 border rounded-md text-sm ${pagination.page === 1 ? "text-gray-400 border-gray-200 cursor-not-allowed" : "text-gray-700 hover:bg-gray-50 border-gray-300"}`}
                >
                  Prev
                </button>
                {Array.from({ length: pagination.total_pages || 1 }).map(
                  (_, i) => {
                    const pageNum = i + 1;
                    const totalPages = pagination.total_pages || 1;
                    const shouldShow =
                      pageNum === 1 ||
                      pageNum === totalPages ||
                      (pageNum >= pagination.page - 2 &&
                        pageNum <= pagination.page + 2);

                    if (!shouldShow) {
                      if (
                        pageNum === pagination.page - 3 ||
                        pageNum === pagination.page + 3
                      ) {
                        return (
                          <span
                            key={`ellipsis-${i}`}
                            className="px-2 text-gray-400"
                          >
                            ...
                          </span>
                        );
                      }
                      return null;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`px-3 py-1 border rounded-md text-sm ${pagination.page === pageNum ? "bg-gray-900 text-white border-gray-900" : "text-gray-700 hover:bg-gray-50 border-gray-300"}`}
                      >
                        {pageNum}
                      </button>
                    );
                  }
                )}
                <button
                  disabled={pagination.page >= (pagination.total_pages || 1)}
                  onClick={() =>
                    setPage((p) => Math.min(pagination.total_pages || 1, p + 1))
                  }
                  className={`px-3 py-1 border rounded-md text-sm ${pagination.page >= (pagination.total_pages || 1) ? "text-gray-400 border-gray-200 cursor-not-allowed" : "text-gray-700 hover:bg-gray-50 border-gray-300"}`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Doctor Modal */}
      <AddDoctorModal
        mounted={modalMounted}
        show={showAddModal}
        onClose={handleCloseAddModal}
        onSubmit={handleAddDoctor}
        loading={submitting}
      />

      {/* Notification Toast */}
      {notification && (
        <NotificationToast
          mounted={notificationMounted}
          notification={notification}
          onClose={handleCloseNotification}
        />
      )}
    </div>
  );  
};

export default DoctorList;
